import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import axios from "axios";
import "../CSS/AttendanceMultiple.css";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import 'primeflex/primeflex.css';
import {
    Box,
    Typography,
    TextField,
    Paper,
  } from "@mui/material";
  import { DataGrid } from "@mui/x-data-grid";

const AttendanceMultiple = () => {
    
    const [fromDate, setFromDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1));
    const [toDate, setToDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0));
    const [attendanceData, setAttendanceData] = useState([]);
    const [selectedGender, setSelectedGender] = useState('');
    const [selectedSport, setSelectedSport] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [showFilters, setShowFilters] = useState(false);

    const chartRef = React.useRef();

    const pieChartRef = React.useRef();
   
    const tooltipRef = useRef();
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;

    const [filters, setFilters] = useState({
        global: { value: null, matchMode: 'contains' },
        date: { value: null, matchMode: 'equals' },
        status: { value: null, matchMode: 'equals' },
        gender: { value: null, matchMode: 'equals' },
        sport: { value: null, matchMode: 'equals' },   
        category: {value: null, matchMode: 'equals'},
    });
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    useEffect(() => {
        fetchAttendanceData();
    }, [fromDate, toDate]);

    const [filteredData1, setFilteredData] = useState([]);
    const fetchAttendanceData = async () => {
        try {
            const response = await axios.get("https://us-central1-balajibadmintonacademy.cloudfunctions.net/api/getAttendance");
            
            // Preprocess the data to add presentCount and leaveCount
            const processedData = response.data.map((entry) => {
                const presentCount = entry.status === "Present" ? 1 : 0;
                const leaveCount = entry.status === "Leave" ? 1 : 0;
                const halfdayCount = entry.status === "Half Day" ? 0.5 : 0;
                return { ...entry, presentCount, leaveCount, halfdayCount };
            });
    
            setAttendanceData(processedData);
            setFilteredData(processedData);
        } catch (error) {
            console.error("Error fetching attendance data:", error);
        }
    };
    
       
    

    // Filter data based on selected date range
    const filteredData = attendanceData.filter((entry) => {
        const entryDate = new Date(entry.date.split("/").reverse().join("/"));
        const dateMatch = entryDate >= fromDate && entryDate <= toDate;
        const genderMatch = selectedGender ? entry.gender === selectedGender : true;
        const sportMatch = selectedSport ? entry.sport === selectedSport : true;
        const categoryMatch = selectedCategory ? entry.category === selectedCategory : true;
        
        return dateMatch && genderMatch && sportMatch && categoryMatch;
    }).map((entry) => {
        const presentCount = entry.status === "Present" ? 1 : 0;
        const leaveCount = entry.status === "Leave" ? 1 : 0;
        const halfdayCount = entry.status === "Half Day" ? 0.5 : 0;
        return { ...entry, presentCount, leaveCount, halfdayCount };
    });
    

    // Aggregate data by date and status
    const aggregatedData = d3.rollups(
        filteredData,
        (entries) => {
            const statusCount = { Present: 0, Leave: 0, HalfDay: 0  }; // Initialize status count
            entries.forEach((entry) => {
                if (entry.status === "Present") statusCount.Present += 1;
                if (entry.status === "Leave") statusCount.Leave += 1;
                if (entry.status === "Half Day") statusCount.HalfDay += 0.5;
            });
            return statusCount; // Return the aggregated status count for each date
        },
        (d) => d.date
    );

    // Transform aggregated data into chart-friendly format
    const chartData = aggregatedData.map(([date, statusCounts]) => ({
        date,
        Present: statusCounts.Present || 0,
        Leave: statusCounts.Leave || 0,
        HalfDay: statusCounts.HalfDay || 0,
    }));

    const totalStatus = {
        Present: d3.sum(chartData, (d) => d.Present),
        Leave: d3.sum(chartData, (d) => d.Leave),
        HalfDay: d3.sum(chartData, (d)=> d.HalfDay),
    };

    const totalPages = Math.ceil(chartData.length / rowsPerPage);

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    const paginatedData = chartData.slice(
        (currentPage - 1) * rowsPerPage,
        currentPage * rowsPerPage
    );

    useEffect(() => {
        if (!chartRef.current) return;
    
        const svg = d3.select(chartRef.current);
        const margin = { top: 20, right: 30, bottom: 40, left: 50 };
        const width = 600 - margin.left - margin.right;
        const height = 400 - margin.top - margin.bottom;
    
        svg.selectAll("*").remove(); // Clear any previous content
    
        if (chartData.length === 0) return;
        
        // Scales
        const x = d3
            .scaleBand()
            .domain(chartData.map((d) => d.date))
            .range([0, width])
            .padding(0.2);
    
        const y = d3
            .scaleLinear()
            .domain([0, d3.max(chartData, (d) => d.Present + d.Leave + d.HalfDay)])
            .nice()
            .range([height, 0]);
    
        const color = d3
            .scaleOrdinal()
            .domain(["Present", "Leave", "Half Day"])
            .range(["#008000", "#f44336", "#808080"]); // Add grey for Half Day
    
        const g = svg
            .append("g")
            .attr("transform", `translate(${margin.left},${margin.top})`);
    
        // Axes
        g.append("g")
            .attr("transform", `translate(0,${height})`)
            .call(d3.axisBottom(x));
    
        g.append("g").call(d3.axisLeft(y));
    
        // Bars
        const bars = g
            .selectAll("g.bar-group")
            .data(chartData)
            .enter()
            .append("g")
            .attr("class", "bar-group")
            .attr("transform", (d) => `translate(${x(d.date)},0)`);
    
        bars.selectAll("rect")
            .data((d) => [
                { status: "Present", value: d.Present, date: d.date },
                { status: "Leave", value: d.Leave, date: d.date },
                { status: "Half Day", value: d.HalfDay, date: d.date },
            ])
            .enter()
            .append("rect")
            .attr("x", (d, i) => (x.bandwidth() / 3) * i) // Adjust for 3 bars per group
            .attr("y", y(0)) // Start from 0 for animation
            .attr("width", x.bandwidth() / 3) // Adjust width for 3 bars
            .attr("height", 0) // Start height from 0
            .attr("fill", (d) => color(d.status))
            .on("mouseover", function (event, d) {
                d3.select(this).attr("opacity", 0.7); // Change opacity on hover
    
                // Set the tooltip content and position
                tooltipRef.current.style.visibility = "visible";
                tooltipRef.current.innerHTML = `
                    <strong>Date:</strong> ${d.date}<br/>
                    <strong>Status:</strong> ${d.status}<br/>
                    <strong>Count:</strong> ${d.value}
                `;
    
                // Position the tooltip near the mouse cursor
                tooltipRef.current.style.left = `${event.pageX + 10}px`;
                tooltipRef.current.style.top = `${event.pageY - 30}px`;
            })
            .on("mouseout", function () {
                d3.select(this).attr("opacity", 1);
                tooltipRef.current.style.visibility = "hidden";
            })
            .transition()
            .duration(2000)
            .attr("y", (d) => y(d.value))
            .attr("height", (d) => height - y(d.value)); // Calculate height based on value
    }, [chartData]);
    

    useEffect(() => {
        if (!pieChartRef.current) return;
    
        const svg = d3.select(pieChartRef.current);
        const radius = 100;
        const margin = { top: 10, right: 10, bottom: 10, left: 10 };
        const width = 300 - margin.left - margin.right;
        const height = 300 - margin.top - margin.bottom;
    
        svg.selectAll("*").remove();
    
        const arc = d3.arc().innerRadius(0).outerRadius(radius);
        const pie = d3.pie().value((d) => d.value);
    
        // Add color for the new Half Day status
        const color = d3.scaleOrdinal().range(["#008000", "#f44336", "#808080"]); // Green, Red, Grey
    
        // Include Half Day data in the calculation
        const pieData = pie([
            { status: "Present", value: totalStatus.Present },
            { status: "Leave", value: totalStatus.Leave },
            { status: "Half Day", value: totalStatus.HalfDay || 0 }, // Default to 0 if undefined
        ]);
    
        const g = svg
            .append("g")
            .attr("transform", `translate(${width / 2},${height / 2})`);
    
        const arcs = g
            .selectAll(".arc")
            .data(pieData)
            .enter()
            .append("g")
            .attr("class", "arc");
    
        arcs
            .append("path")
            .attr("d", arc)
            .attr("fill", (d) => color(d.data.status))
            .on("mouseover", function (event, d) {
                d3.select(this).attr("opacity", 0.7); // Change opacity on hover
    
                // Set the tooltip content and position
                tooltipRef.current.style.visibility = "visible";
                tooltipRef.current.innerHTML = `
                    <strong>Status:</strong> ${d.data.status}<br/>
                    <strong>Count:</strong> ${d.data.value}
                `;
    
                // Position the tooltip near the mouse cursor
                tooltipRef.current.style.left = `${event.pageX + 10}px`;
                tooltipRef.current.style.top = `${event.pageY - 30}px`;
            })
            .on("mouseout", function () {
                d3.select(this).attr("opacity", 1); // Reset opacity
                tooltipRef.current.style.visibility = "hidden"; // Hide the tooltip
            })
            .transition() // Add transition
            .duration(3000)
            .attrTween("d", function (d) {
                const interpolate = d3.interpolate({ startAngle: 0, endAngle: 0 }, d);
                return function (t) {
                    return arc(interpolate(t));
                };
            });
    
        // Add percentage labels inside the pie chart
        arcs
            .append("text")
            .attr("transform", (d) => `translate(${arc.centroid(d)})`)
            .attr("dy", ".35em")
            .style("text-anchor", "middle")
            .style("fill", "white")
            .text((d) => {
                const total = totalStatus.Present + totalStatus.Leave + (totalStatus.HalfDay || 0);
                return `${((d.data.value / total) * 100).toFixed(1)}%`;
            });
    }, [totalStatus]);
    
    

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        _filters['global'].value = value;
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const dateFilterTemplate = (options) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e) => options.filterCallback(e.value)}
                dateFormat="dd-mm-yy"
                showButtonBar
                style={{ backgroundColor: "lightblue" }}
                className="calendarfilter custom-calendar form-control1"
            />
        );
    };

    const statusFilterTemplate = (options) => {
        return (
            <select
                value={options.value}
                onChange={(e) => options.filterCallback(e.value)}
                className="p-inputtext p-component"
               
            >
                <option value="">All</option>
                <option value="Present">Present</option>
                <option value="Leave">Leave</option>
                <option value="Half Day">Half Day</option>
            </select>
        );
    };

    const genderFilterTemplate = (options) => (
        <select
            value={options.value}
            onChange={(e) => options.filterCallback(e.target.value)}
            className="p-inputtext p-component"
        >
            <option value="">All</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
        </select>
    );
    
    const sportFilterTemplate = (options) => (
        <select
            value={options.value}
            onChange={(e) => options.filterCallback(e.target.value)}
            className="p-inputtext p-component"
        >
            <option value="">All</option>
            <option value="Badminton">Badminton</option>
            <option value="Yoga">Yoga</option>
            <option value="Dance">Dance</option>
            {/* Add more sports as needed */}
        </select>
    );
    

    const toggleFilters = () => {
        setShowFilters((prev) => !prev);
    };

    const handleSearchChange = (e) => {
        const value = e.target.value.toLowerCase();
        setGlobalFilterValue(value);
    
        const filtered = attendanceData.filter((entry) =>
          entry.date.toLowerCase().includes(value) ||
          entry.status.toLowerCase().includes(value) ||
          entry.sport.toLowerCase().includes(value) ||
          entry.gender.toLowerCase().includes(value)
        );
    
        setFilteredData(filtered);
      };
    
      const columns = [
        { field: "date", headerName: "Date", width: 150 },
        { field: "status", headerName: "Status", width: 150 },
        { field: "sport", headerName: "Sport", width: 150 },
        { field: "gender", headerName: "Gender", width: 150 },
        { field: "category", headerName: "Category", width: 150 },
        { field: "presentCount", headerName: "No. of Present", width: 150 },
        { field: "leaveCount", headerName: "No. of Leave", width: 150 },
        { field: "halfdayCount", headerName: "No. of Half Day", width: 150 },
      ];
      
    return (
        <div className="Attendace-dashboard">
            <h2 className="text-center mb-4">Attendance Dashboard</h2>
            <div className="Attendacedate" style={{ display: "flex", marginBottom: "20px" }}>
                <div>
                    <label>From Date:</label>
                    <DatePicker selected={fromDate} onChange={setFromDate} style={{borderColor:"black", width: "50px"}} className="form-control1"/>
                </div>
                <div>
                    <label>To Date:</label>
                    <DatePicker selected={toDate} onChange={setToDate} className="form-control1"/>
                </div>
                <div className="additional-filters">
                    <Button 
                        label={showFilters ? '-' : '+'} 
                        onClick={toggleFilters} 
                        className="p-button-rounded p-button-info" 
                        style={{backgroundColor: "white", borderColor:"white"}}
                    />
                </div>
            </div>
            {showFilters && (
            <div className="Attendacedate" style={{ display: "flex", marginBottom: "20px" }}>
            <div>
                        <label>Gender:</label>
                        {genderFilterTemplate({
                            value: selectedGender,
                            filterCallback: setSelectedGender
                        })}
                    </div>
                    <div>
                        <label>Sport:</label>
                        {sportFilterTemplate({
                            value: selectedSport,
                            filterCallback: setSelectedSport
                        })}
                    </div>
                    <label htmlFor="category">Category:</label>
                    <select
                        id="category"
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="p-inputtext p-component"
                    >
                        <option value="">Select Category</option>
                        <option value="Single">Single</option>
                        <option value="Membership">Membership</option>
                    </select>
                </div>          
            )}

            <div className="cards-container" style={{ display: "flex", gap: "20px", marginBottom: "20px", width: "50%" }}>
                <div className="card present-card" style={{ flex: 1, padding: "20px", border: "1px solid #ccc", borderRadius: "10px",background: "#ffffff",textAlign: "center", }}>
                    <h3>Total Present</h3>
                    <p style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#00796b" }}>{totalStatus.Present}</p>
                </div>
                <div className="card leave-card" style={{ flex: 1, padding: "20px", border: "1px solid #ccc", borderRadius: "10px",background: "#ffffff",textAlign: "center" }}>
                    <h3>Total Leave</h3>
                    <p style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#c62828" }}>{totalStatus.Leave}</p>
                </div>
                <div className="card half-day-card" style={{ flex: 1, padding: "20px", border: "1px solid #ccc", borderRadius: "10px",background: "#ffffff",textAlign: "center" }}>
                    <h3>Total Half Day</h3>
                    <p style={{ fontSize: "1.5rem", fontWeight: "bold", color: "#f9a825" }}>{totalStatus.HalfDay}</p>
                </div>
            </div>

          
            <h2 className="text-center mb-4">Daily Analysis</h2> 
            <div className="chart-legend" style={{ display: "flex", alignItems: "end", marginTop: "20px", right: "0" }}>
                <div style={{ display: "flex", alignItems: "center", marginRight: "15px" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#008000",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Present</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#f44336",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Leave</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#808080",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Half Day</span>
                </div>
            </div>
           
            <div
                ref={tooltipRef}
                style={{
                    position: "absolute",
                    backgroundColor: "rgba(0, 0, 0, 0.7)",
                    color: "white",
                    padding: "8px",
                    borderRadius: "4px",
                    visibility: "hidden",
                    pointerEvents: "none",
                    zIndex: 10,
                }}
            ></div>

            <svg className="Attendancechartref" ref={chartRef} width="600" height="400"></svg><br></br>
            <br></br>
            <h2 className="text-center mb-4">Overall Analysis</h2>  
            <div className="chart-legend" style={{ display: "flex", alignItems: "end", marginTop: "20px", right: "0" }}>
                <div style={{ display: "flex", alignItems: "center", marginRight: "15px" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#008000",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Present</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#f44336",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Leave</span>
                </div>
                <div style={{ display: "flex", alignItems: "center" }}>
                    <span
                        style={{
                            width: "15px",
                            height: "15px",
                            backgroundColor: "#808080",
                            display: "inline-block",
                            marginRight: "5px",
                            borderRadius: "3px",
                        }}
                    ></span>
                    <span>Half Day</span>
                </div>
            </div>

            <svg className="Attendancepiechartref" ref={pieChartRef} width="300" height="300"></svg>
            
            <Box sx={{ padding: 4 }}>
    
            <h2 className="text-center mb-4">Daily Analysis</h2> 
      
      <Paper sx={{ height: 500}}>
        <DataGrid
          rows={filteredData1}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 20]}
          disableSelectionOnClick
          autoHeight
          sortingOrder={['asc', 'desc']}
          className="dategridmultipleattendance"
          
        />
      </Paper>
    </Box>
        </div>
    );
};

export default AttendanceMultiple;
